import { PatientInsuranceDeclarationDto, RegionalBranch } from '../../../types/insuranceDeclaration';
import { insuranceDeclarationApi } from '../../../api/insuranceDeclaration/insuranceDeclarationApi';
import { Box, FormControl, FormControlLabel, IconButton, InputLabel, makeStyles, MenuItem, Radio, RadioGroup, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import i18nNamespaces from '../../../const/i18nNamespaces';
import { useEffect, useMemo, useState } from 'react';
import { CustomSelect, Loading } from '../..';
import { BACKGROUND_PRIMARY } from '../../../const/colors';
import { selectAuthUserData } from '../../../store/auth';
import { useAppSelector } from '../../../hooks/customReduxHooks';

type InsuranceDeclarationProps = {
    submitButton?: React.ReactNode;
    onDeclarationChange?: (declaration: PatientInsuranceDeclarationDto | null) => void;
}

const useStyles = makeStyles(theme => ({
    container: {
        backgroundColor: 'white',
        borderTop: `8px solid ${BACKGROUND_PRIMARY}`,
        margin: '0 -20px',
        padding: '20px 20px 10px 20px',
    },
    submitButtonWrapper: {
        backgroundColor: 'white',
        borderTop: `8px solid ${BACKGROUND_PRIMARY}`,
        margin: '0 -20px',
        padding: '10px 20px 0 20px',
    },
    filledBranchWrapper: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16
    }
}));

enum InsuranceOption {
    INSURED = 'insured',
    NOT_INSURED = 'notInsured',
}

const InsuranceDeclaration = ({submitButton, onDeclarationChange}: InsuranceDeclarationProps) => {
    const classes = useStyles();
    const {t} = useTranslation(i18nNamespaces.PRESCRIPTIONS);
    const authUserData = useAppSelector(selectAuthUserData);
    const [loading, setLoading] = useState<boolean>(false);
    const [insuranceOption, setInsuranceOption] =
        useState<InsuranceOption | null>(null);
    const [regionalBranchList, setRegionalBranchList] = useState<
        RegionalBranch[]
    >([]);
    const [selectedRegionalBranchCode, setSelectedRegionalBranchCode] =
        useState<RegionalBranch['code'] | null>(null);
        
    const isSendingDisabled = useMemo(
        () =>
            (!selectedRegionalBranchCode &&
                insuranceOption === InsuranceOption.INSURED) ||
            !insuranceOption,
        [selectedRegionalBranchCode, insuranceOption],
    );
    const fetchRegionalBranches = async () => {
        try {
            const {data} = await insuranceDeclarationApi.getNfzRegionalBranch();
            setRegionalBranchList(data.result);
        } catch (err) {
            console.error(err);
        }
    };


    useEffect(() => {
        fetchRegionalBranches();
        fetchInsuranceDeclaration();
    }, []);

    useEffect(() => {
        if (!insuranceOption) {
            onDeclarationChange?.(null);
            return;
        }

        if (insuranceOption === InsuranceOption.INSURED && !selectedRegionalBranchCode) {
            onDeclarationChange?.(null);
            return;
        }

        onDeclarationChange?.({
            hasPublicInsurance: insuranceOption === InsuranceOption.INSURED,
            nfzRegionalBranchCode: selectedRegionalBranchCode ?? '',
        });
    }, [insuranceOption, selectedRegionalBranchCode, onDeclarationChange]);

    const fetchInsuranceDeclaration = async () => {
        try {
            if(!authUserData?.userId) return;
            setLoading(true);
            const {data} = await insuranceDeclarationApi.getInsuranceDeclaration(authUserData?.userId);
            if(data.result.hasPublicInsurance) {
                setSelectedRegionalBranchCode(data.result?.nfzRegionalBranchCode ?? null);
                setInsuranceOption(InsuranceOption.INSURED);
            } else{
                setInsuranceOption(InsuranceOption.NOT_INSURED);
            }
        } catch(err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }

    return(
        <Box>
            <Loading loading={loading} withBackdrop/>
            <RadioGroup
                    className={classes.container}
                    aria-label="insurance"
                    name="insurance"
                    value={insuranceOption}
                    onChange={e => {
                        const value = e.target.value as InsuranceOption;
                        setInsuranceOption(value);
                        if (value === InsuranceOption.NOT_INSURED) {
                            setSelectedRegionalBranchCode(null);
                        }
                    }}
                >
                    <h2 style={{marginTop: 0, marginBottom: 5}}>{t('insuranceDeclaration')}</h2>
                    <FormControlLabel
                        value={InsuranceOption.INSURED}
                        control={<Radio />}
                        label={t('insured')}
                    />
                    {insuranceOption === InsuranceOption.INSURED &&
                        regionalBranchList.length && (
                            <>
                                <FormControl
                                    variant="outlined"
                                    style={{minWidth: 500, margin: '15px 0'}}
                                >
                                    <InputLabel id="selectRegionalBranchLabel">
                                        {t('selectRegionalBranch')}
                                    </InputLabel>
                                    <CustomSelect
                                        labelId={'selectRegionalBranchLabel'}
                                        value={selectedRegionalBranchCode ?? ''}
                                        label={t('selectRegionalBranch')}
                                        onChange={e =>
                                            setSelectedRegionalBranchCode(
                                                e.target
                                                    .value as RegionalBranch['code'],
                                            )
                                        } //
                                        fullWidth
                                    >
                                        {regionalBranchList.map(
                                            regionalBranchItem => (
                                                <MenuItem
                                                    value={
                                                        regionalBranchItem.code
                                                    }
                                                    key={
                                                        regionalBranchItem.code
                                                    }
                                                >
                                                    {regionalBranchItem.name}
                                                </MenuItem>
                                            ),
                                        )}
                                    </CustomSelect>
                                </FormControl>
                            </>
                        )}
                    <FormControlLabel
                        value={InsuranceOption.NOT_INSURED}
                        control={<Radio />}
                        label={t('notInsured')}
                    />
                </RadioGroup>
                {!isSendingDisabled &&
                    <Box className={classes.submitButtonWrapper}>
                        {submitButton}
                    </Box>
                }   
                
        </Box>
    )
}

export default InsuranceDeclaration;